import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Checkbox, Input, Spin, Upload} from 'antd';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import cloudIcon from 'images/cloud-upload.svg';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {CREATE_THEME, GET_THEME, UPDATE_THEME} from './resolvers';
import {
  FEATURE_BRANCH,
  FEATURE_DESCRIPTION,
  FEATURE_OPTIONS,
  buildBranchInput,
  errorMessage,
  featureLabel,
  humanizeField,
  isGqlError,
  missingRequiredFields,
  readMutation,
} from './constants';
import {useFilterStructure} from './useFilterStructure';
import {FilterPanel} from './FilterPanel';
import styles from './themes.module.scss';

const isDev = String(process.env.REACT_APP_API_BASE_URL || '').includes(
  '.daba-dev.',
);
const BUCKET = isDev
  ? 'dabafinancialinstruments-nonprod'
  : 'dabafinancialinstruments-prod';

const EMPTY = {icon: '', name: '', description: '', features: [], filter: {}};

/**
 * Create/edit an instrument theme.
 *
 * A page rather than a modal: each selected feature adds a metadata-driven
 * panel of 17–32 fields, so a fully configured two-feature theme is ~2000px
 * of form. That does not belong on a dialog surface.
 *
 * Selected features and populated branches must agree exactly, so the branch
 * form state is kept per feature and the outgoing envelope is assembled at
 * submit time from only the selected features. On update, `filter` is a full
 * envelope replacement — every selected branch is sent complete.
 */
export const ThemeForm = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const backToList = () => navigate('/dashboard/instrument-themes');
  const isEdit = Boolean(id);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.InstrumentThemes),
  );

  const {
    structures,
    loading: structureLoading,
    error: structureError,
  } = useFilterStructure();

  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [seeded, setSeeded] = useState(!isEdit);

  const {data, loading: themeLoading} = useQuery(GET_THEME, {
    variables: {id},
    skip: !isEdit,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [createTheme, {loading: creating}] = useMutation(CREATE_THEME, {
    errorPolicy: 'all',
  });
  const [updateTheme, {loading: updating}] = useMutation(UPDATE_THEME, {
    errorPolicy: 'all',
  });

  // Seed the form once from the fetched theme. Metadata defaults are applied
  // only to brand-new branches, never over a value the admin already saved.
  useEffect(() => {
    if (seeded) return;
    const result = data?.adminGetInstrumentTheme;
    if (!result) return;
    if (isGqlError(result)) {
      toast.error(errorMessage(result));
      return;
    }
    const theme = result.data;
    setForm({
      icon: theme.icon || '',
      name: theme.name || '',
      description: theme.description || '',
      features: theme.features || [],
      filter: {
        financialInstrument: stripTypename(theme.filter?.financialInstrument),
        collection: stripTypename(theme.filter?.collection),
      },
    });
    setSeeded(true);
  }, [data, seeded]);

  const applyDefaults = (feature, existing) => {
    const structure = structures[feature];
    if (!structure) return existing || {};
    const branch = {...(existing || {})};
    structure.fields.forEach(field => {
      if (
        branch[field.name] === undefined &&
        field.defaultValue !== undefined &&
        field.defaultValue !== null
      ) {
        branch[field.name] = field.defaultValue;
      }
    });
    return branch;
  };

  const toggleFeature = (feature, checked) => {
    setForm(previous => {
      const features = checked
        ? [...previous.features, feature]
        : previous.features.filter(item => item !== feature);
      const branchKey = FEATURE_BRANCH[feature];
      const filter = {...previous.filter};
      if (checked)
        filter[branchKey] = applyDefaults(feature, filter[branchKey]);
      // Deselecting a feature strips its branch — no stale hidden data.
      else delete filter[branchKey];
      return {...previous, features, filter};
    });
  };

  const setBranchField = (feature, name, value) => {
    const branchKey = FEATURE_BRANCH[feature];
    setForm(previous => ({
      ...previous,
      filter: {
        ...previous.filter,
        [branchKey]: {...previous.filter[branchKey], [name]: value},
      },
    }));
  };

  const handleIconUpload = async info => {
    const file = info.file?.originFileObj || info.file;
    if (!file) return;
    try {
      setUploading(true);
      const url = await UploadDocumentToBucket(
        file,
        () => {},
        BUCKET,
        true,
        'instrument-themes',
      );
      setForm(previous => ({...previous, icon: url}));
    } catch (uploadError) {
      toast.error('Could not upload that image');
    } finally {
      setUploading(false);
    }
  };

  const validation = useMemo(() => {
    const problems = [];
    if (!form.icon?.trim()) problems.push('An icon is required');
    if (!form.name?.trim()) problems.push('A name is required');
    if (!form.description?.trim()) problems.push('A description is required');
    if (!form.features.length) problems.push('Select at least one feature');
    form.features.forEach(feature => {
      const structure = structures[feature];
      if (!structure) return;
      const branch = form.filter[FEATURE_BRANCH[feature]] || {};
      const missing = missingRequiredFields(structure.fields, branch);
      if (missing.length)
        problems.push(
          `${featureLabel(feature)}: ${missing
            .map(humanizeField)
            .join(', ')} required`,
        );
    });
    return problems;
  }, [form, structures]);

  const buildEnvelope = () => {
    const filter = {};
    form.features.forEach(feature => {
      const structure = structures[feature];
      const branchKey = FEATURE_BRANCH[feature];
      filter[branchKey] = buildBranchInput(
        structure?.fields || [],
        form.filter[branchKey] || {},
      );
    });
    return filter;
  };

  const handleSubmit = () => {
    if (validation.length) {
      toast.error(validation[0]);
      return;
    }
    const input = {
      icon: form.icon.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      features: form.features,
      filter: buildEnvelope(),
    };

    const request = isEdit
      ? updateTheme({variables: {id, input}})
      : createTheme({variables: {input}});

    request
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          isEdit ? 'updateInstrumentTheme' : 'createInstrumentTheme',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        backToList();
      })
      .catch(error => toast.error(error?.message));
  };

  const disabled = !tabOperations?.[isEdit ? 'update' : 'create'];

  if (structureLoading || (isEdit && themeLoading && !seeded))
    return (
      <div className={styles.centered}>
        <Spin />
      </div>
    );

  return (
    <div className={styles.container}>
      <button type="button" className={styles.back} onClick={backToList}>
        ← All themes
      </button>

      <div className={styles.pageHead}>
        <div>
          <h1>{isEdit ? form.name || 'Edit theme' : 'New theme'}</h1>
          <p>
            An illustrated preset mobile applies when investors browse. Saving
            makes it live immediately — there is no draft state.
          </p>
        </div>
        {form.features.length > 0 && (
          <div className={styles.chips}>
            {form.features.map(feature => (
              <span
                key={feature}
                className={`${styles.chip} ${styles.chipBrand}`}>
                {featureLabel(feature)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        {structureError && (
          <Alert
            className={styles.alert}
            type="error"
            showIcon
            message="Could not load the filter structure"
            description={errorMessage(structureError, structureError?.message)}
          />
        )}

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <h3>Details</h3>
            <p>The icon and name investors see on the browse screen.</p>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.detailsGrid}>
              <div className={styles.iconField}>
                <Upload
                  accept=".png,.jpg,.jpeg,.svg"
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={() => false}
                  disabled={disabled}
                  onChange={handleIconUpload}>
                  <div className={styles.iconUpload}>
                    {form.icon ? (
                      <img
                        src={form.icon}
                        alt="icon"
                        className={styles.iconPreview}
                      />
                    ) : (
                      <>
                        <img src={cloudIcon} alt="" />
                        <span>{uploading ? 'Uploading…' : 'Upload icon'}</span>
                      </>
                    )}
                  </div>
                </Upload>
                {form.icon && (
                  <Button
                    label="Change"
                    type="secondary"
                    onClick={() =>
                      setForm(previous => ({...previous, icon: ''}))
                    }
                  />
                )}
              </div>

              <div className={styles.detailInputs}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    Name<span className={styles.required}>*</span>
                  </label>
                  <Input
                    value={form.name}
                    disabled={disabled}
                    placeholder="Banking opportunities"
                    onChange={event =>
                      setForm(previous => ({
                        ...previous,
                        name: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    Description<span className={styles.required}>*</span>
                  </label>
                  <Input.TextArea
                    rows={3}
                    value={form.description}
                    disabled={disabled}
                    placeholder="Public equities and curated collections in banking"
                    onChange={event =>
                      setForm(previous => ({
                        ...previous,
                        description: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <h3>Where it applies</h3>
            <p>
              Each feature you pick adds its own filter panel below. Pick at
              least one.
            </p>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.featureRow}>
              {FEATURE_OPTIONS.map(option => {
                const on = form.features.includes(option.value);
                return (
                  // The whole card is the target rather than a 16px checkbox,
                  // and it says what the choice actually changes.
                  <label
                    key={option.value}
                    className={`${styles.featureCard} ${
                      on ? styles.featureCardOn : ''
                    }`}>
                    <Checkbox
                      disabled={disabled}
                      checked={on}
                      onChange={event =>
                        toggleFeature(option.value, event.target.checked)
                      }
                    />
                    <span className={styles.featureCardText}>
                      <strong>{option.label}</strong>
                      <span>{FEATURE_DESCRIPTION[option.value]}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {form.features.map(feature => (
          <div key={feature} className={styles.card}>
            <FilterPanel
              structure={structures[feature]}
              disabled={disabled}
              values={form.filter[FEATURE_BRANCH[feature]]}
              onChange={(name, value) => setBranchField(feature, name, value)}
            />
          </div>
        ))}
      </div>

      {/* Sticky, so the outstanding-work count and the save button stay
          reachable without scrolling past every filter panel. */}
      <div className={styles.stickyFooter}>
        <div
          className={`${styles.footerStatus} ${
            validation.length ? '' : styles.footerReady
          }`}
          // Only the first problem fits the footer; the rest stay reachable on
          // the tooltip rather than being dropped.
          title={validation.join('\n')}>
          {validation.length ? (
            <>
              <b>{validation.length}</b> still to finish — {validation[0]}
              {validation.length > 1 ? ` (+${validation.length - 1} more)` : ''}
            </>
          ) : (
            <>Ready to save · goes live immediately</>
          )}
        </div>
        <Button label="Cancel" type="secondary" onClick={backToList} />
        <Button
          label={
            creating || updating
              ? 'Saving…'
              : isEdit
              ? 'Save changes'
              : 'Create theme'
          }
          disabled={
            disabled || creating || updating || Boolean(validation.length)
          }
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

/** Filter inputs must not carry `__typename` back into a mutation. */
const stripTypename = branch => {
  if (!branch) return undefined;
  // Drop `__typename` and null-valued fields so they read as "unset" in the
  // editor and never reach a filter input.
  return Object.fromEntries(
    Object.entries(branch).filter(
      ([key, value]) => key !== '__typename' && value !== null,
    ),
  );
};

export default ThemeForm;
